import React, { useState, useRef, useMemo } from 'react';
import { calcularPlano, gerarPdf } from '../api';
import { usePlanoEditor } from '../hooks/usePlanoEditor';
import Visualizacao2D from './Visualizacao2D';
import Visualizacao3D from './Visualizacao3D';
import { centralizacaoConjuntoCem2x2 } from './centralizacaoCem2x2';
import ModalPdf from './ModalPdf';
import './DetalheVolume.css';
import { parametrosModalidade } from '../modalidades';

function hasWebGL() {
  try {
    const canvas = document.createElement('canvas');
    return !!(
      window.WebGLRenderingContext &&
      (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'))
    );
  } catch {
    return false;
  }
}

// ── Bloco de informações do volume (read-only) ────────────────────────────────

function InfoVolume({ volume }) {
  return (
    <div className="pal-card">
      <h3 className="pal-card-title">Volume</h3>
      <div className="pal-info-grid">
        <span className="pal-info-label">Produto</span>
        <span className="pal-info-value pal-info-produto">{volume.produto}</span>

        {volume.descricao && (
          <>
            <span className="pal-info-label">Descrição</span>
            <span className="pal-info-value">{volume.descricao}</span>
          </>
        )}

        <span className="pal-info-label">Comprimento</span>
        <span className="pal-info-value pal-info-mono">{volume.comprimento_mm} mm</span>

        <span className="pal-info-label">Largura</span>
        <span className="pal-info-value pal-info-mono">{volume.largura_mm} mm</span>

        <span className="pal-info-label">Altura</span>
        <span className="pal-info-value pal-info-mono">{volume.altura_mm} mm</span>

        <span className="pal-info-label">Peso bruto</span>
        <span className="pal-info-value pal-info-mono">{volume.peso_bruto_kg} kg</span>
      </div>
    </div>
  );
}

// ── Editor do plano ───────────────────────────────────────────────────────────

function PlanoEditor({ campos, setField, disabled }) {
  return (
    <div className="pal-card">
      <h3 className="pal-card-title">Plano</h3>
      <div className="pal-editor-grid">
        <label className="pal-editor-label">Caixas por camada</label>
        <input
          type="number"
          className="pal-editor-input"
          min={1}
          disabled={disabled}
          value={campos.caixasPorCamada}
          onChange={e => setField('caixasPorCamada', Math.max(1, parseInt(e.target.value, 10) || 1))}
        />

        <label className="pal-editor-label">Nº de camadas</label>
        <input
          type="number"
          className="pal-editor-input"
          min={1}
          disabled={disabled}
          value={campos.numCamadas}
          onChange={e => setField('numCamadas', Math.max(1, parseInt(e.target.value, 10) || 1))}
        />

        <label className="pal-editor-label">Orientação</label>
        <div className="pal-radio-group">
          <label className="pal-radio-label">
            <input
              type="radio"
              value="longitudinal"
              disabled={disabled}
              checked={campos.orientacao === 'longitudinal'}
              onChange={() => setField('orientacao', 'longitudinal')}
            />
            <span>Longitudinal</span>
          </label>
          <label className="pal-radio-label">
            <input
              type="radio"
              value="atravessada"
              disabled={disabled}
              checked={campos.orientacao === 'atravessada'}
              onChange={() => setField('orientacao', 'atravessada')}
            />
            <span>Atravessada</span>
          </label>
        </div>

        <label className="pal-editor-label">Sobreposição (mm)</label>
        <input
          type="number"
          className="pal-editor-input"
          min={0}
          max={200}
          disabled={disabled}
          value={campos.sobreposicaoMm}
          onChange={e => setField('sobreposicaoMm', Math.min(200, Math.max(0, parseInt(e.target.value, 10) || 0)))}
        />
      </div>
    </div>
  );
}

// ── Estatísticas do plano ─────────────────────────────────────────────────────

function PlanoStats({ plano, campos, volume, parametros, modalidade }) {
  // Deriva stats localmente a partir dos campos quando o plano ainda não chegou
  const n       = campos.caixasPorCamada;
  const camadas = campos.numCamadas;
  const altTotal  = plano?.altura_total_mm  ?? camadas * (volume?.altura_mm ?? 0);
  const pesoTotal = plano?.peso_total_kg    ?? camadas * n * (volume?.peso_bruto_kg ?? 0);
  const totalCx   = plano?.total_caixas     ?? n * camadas;
  const balanco   = plano?.balanco_lateral_mm ?? 0;
  const balancoComposicao = plano?.balanco_composicao_mm ?? 0;

  const volCx  = n * camadas * (volume?.comprimento_mm ?? 0) * (volume?.largura_mm ?? 0) * (volume?.altura_mm ?? 0);
  const volPal = parametros.comprimentoMm * parametros.larguraMm * altTotal;
  const densidade = volPal > 0 ? ((volCx / volPal) * 100).toFixed(1) : '—';

  return (
    <div className="pal-card">
      <h3 className="pal-card-title">Resultados</h3>
      <div className="pal-stats-grid">
        <div className="pal-stat">
          <span className="pal-stat-label">Total de caixas</span>
          <span className="pal-stat-value pal-stat-destaque">{totalCx}</span>
        </div>
        <div className="pal-stat">
          <span className="pal-stat-label">{modalidade === 'lojas_cem' ? 'Altura do conjunto' : 'Altura total'}</span>
          <span className="pal-stat-value">{Math.round(altTotal + (modalidade === 'lojas_cem' ? parametros.alturaPaleteMm : 0))} mm</span>
        </div>
        <div className="pal-stat">
          <span className="pal-stat-label">Peso total</span>
          <span className="pal-stat-value">{pesoTotal.toFixed(1)} kg</span>
        </div>
        <div className="pal-stat">
          <span className="pal-stat-label">Balanço lateral</span>
          <span className="pal-stat-value">{balanco > 0 ? `${Math.round(balanco)} mm/lado` : '—'}</span>
        </div>
        {modalidade === 'lojas_cem' && balancoComposicao > 0 && (
          <div className="pal-stat">
            <span className="pal-stat-label">Balanço da composição</span>
            <span className="pal-stat-value">{balancoComposicao} mm total</span>
          </div>
        )}
        <div className="pal-stat">
          <span className="pal-stat-label">Densidade vol.</span>
          <span className="pal-stat-value">{densidade}%</span>
        </div>
      </div>
    </div>
  );
}

// ── Painel de alertas ─────────────────────────────────────────────────────────

function AlertasPanel({ alertas }) {
  if (!alertas?.length) return null;

  const classFor = (alerta) => {
    const a = alerta.toLowerCase();
    if (a.includes('acima'))       return 'pal-alerta-erro';
    if (a.includes('tolerância'))  return 'pal-alerta-aviso';
    return 'pal-alerta-info';
  };

  const iconFor = (cls) => {
    if (cls === 'pal-alerta-erro')  return 'ERR';
    if (cls === 'pal-alerta-aviso') return 'AVI';
    return 'ℹ️';
  };

  return (
    <div className="pal-card pal-alertas-card">
      <h3 className="pal-card-title">Alertas</h3>
      <ul className="pal-alertas-lista">
        {alertas.map((a, i) => {
          const cls = classFor(a);
          return (
            <li key={i} className={`pal-alerta-item ${cls}`}>
              <span className="pal-alerta-icon">{iconFor(cls)}</span>
              <span>{a}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// ── Componente raiz: DetalheVolume ────────────────────────────────────────────

function DetalheVolume({ volume, planoInicial, onVoltar, onVolumeAtualizado, codigo81, modalidade = 'embalagem' }) {
  // POC temporário (jul/2026) — codigo81 vem por prop do lote (Paletizacao.jsx); substituir por vínculo ao produto cadastrado no futuro
  const { campos, setField, plano, salvando, pendente, ultimoSalvo, erro, restaurar, salvar } =
    usePlanoEditor(volume.id, planoInicial, modalidade);

  const [restaurando, setRestaurando]   = useState(false);
  const [erroRestaurar, setErroRestaurar] = useState(null);
  const [gerandoPdf, setGerandoPdf]     = useState(false);
  const [statusGeracao, setStatusGeracao] = useState('');
  const [pdfBlobUrl, setPdfBlobUrl]     = useState(null);

  const webGLAvailable = useMemo(() => hasWebGL(), []);
  const viz3dRef = useRef(null);

  // Chama /calcular de novo e restaura o plano sugerido
  const handleRestaurar = async () => {
    setRestaurando(true);
    setErroRestaurar(null);
    try {
      const result = await calcularPlano(volume.id, modalidade);
      if (!result.viavel) {
        setErroRestaurar('Nenhum plano viável encontrado para este volume.');
        return;
      }
      restaurar(result.plano);
    } catch (err) {
      setErroRestaurar(err.message);
    } finally {
      setRestaurando(false);
    }
  };

  const handleGerarPdf = async () => {
    try {
      setGerandoPdf(true);
      setStatusGeracao('Capturando imagens…');
      const palet    = await viz3dRef.current.capturarAngulo('palet');
      const camada1  = await viz3dRef.current.capturarAngulo('camada1');
      const camada2  = await viz3dRef.current.capturarAngulo('camada2');
      const completo = await viz3dRef.current.capturarAngulo('completo');
      setStatusGeracao('Gerando PDF…');
      const blob = await gerarPdf(volume.id, { palet, camada1, camada2, completo }, codigo81.trim() || null, modalidade);
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(URL.createObjectURL(blob));
      if (onVolumeAtualizado) onVolumeAtualizado('gerado_pdf');
    } catch (e) {
      alert('Erro ao gerar PDF: ' + e.message);
    } finally {
      setGerandoPdf(false);
      setStatusGeracao('');
    }
  };

  const pdfHabilitado = webGLAvailable && !!plano?.id && !pendente && !salvando && !erro && !restaurando && !gerandoPdf;
  const pdfTooltip = !webGLAvailable
    ? 'Visualização 3D necessária para gerar PDF'
    : !plano?.id
    ? 'Salve o plano antes de gerar o PDF'
    : erro
    ? 'Corrija o erro ao salvar antes de gerar o PDF'
    : pendente || salvando || restaurando
    ? 'Aguarde o salvamento do plano antes de gerar o PDF'
    : null;

  const alertas = plano?.alertas ?? planoInicial?.alertas ?? [];
  const parametros = parametrosModalidade(modalidade);
  const cem2x2 = modalidade === 'lojas_cem' && campos.orientacao === 'longitudinal'
    && campos.caixasAlinhadas === 2 && campos.caixasGiradas === 2;
  const centralizacaoConjuntoZ = cem2x2
    ? centralizacaoConjuntoCem2x2(
      volume.comprimento_mm, volume.largura_mm, parametros.larguraMm,
      campos.sobreposicaoMm ?? 50, campos.numCamadas
    )
    : 0;

  return (
    <div className="pal-detalhe">
      {/* Topbar */}
      <div className="pal-detalhe-topbar">
        <button className="pal-btn-ghost" onClick={onVoltar}>← Voltar</button>
        <div className="pal-detalhe-titulo">
          <strong>{volume.produto}</strong>
          {volume.descricao && <span className="pal-detalhe-descr"> — {volume.descricao}</span>}
        </div>
        <div className="pal-salvo-status">
          {salvando && <><span className="pal-spinner-sm" /> Salvando…</>}
          {!salvando && !pendente && !erro && ultimoSalvo && (
            <span className="pal-salvo-ok">✓ Salvo</span>
          )}
        </div>
      </div>

      {(erro || erroRestaurar) && (
        <div className="pal-error pal-error-inline">
          <strong>Erro</strong>
          <p>{erro || erroRestaurar}</p>
        </div>
      )}

      {/* Grid principal: esquerda (editor) | direita (SVG) */}
      <div className="pal-detalhe-grid">
        {/* Coluna esquerda */}
        <div className="pal-detalhe-esquerda">
          <InfoVolume volume={volume} />
          <PlanoEditor campos={campos} setField={setField} disabled={gerandoPdf} />
          <PlanoStats plano={plano} campos={campos} volume={volume} parametros={parametros} modalidade={modalidade} />
          {alertas.length > 0 && <AlertasPanel alertas={alertas} />}

          {/* Ações */}
          <div className="pal-detalhe-acoes">
            <button
              className="pal-btn-secondary"
              onClick={handleRestaurar}
              disabled={restaurando || gerandoPdf}
            >
              {restaurando
                ? <><span className="pal-spinner-sm" /> Restaurando…</>
                : '↺ Restaurar sugestão automática'}
            </button>

            <button
              className="pal-btn-primary"
              onClick={salvar}
              disabled={salvando || gerandoPdf}
            >
              {salvando
                ? <><span className="pal-spinner-sm" /> Salvando…</>
                : 'Salvar plano'}
            </button>

            <div className="pal-btn-pdf-wrapper" title={pdfTooltip ?? ''}>
              <button
                className="pal-btn-primary"
                onClick={handleGerarPdf}
                disabled={!pdfHabilitado}
              >
                {gerandoPdf
                  ? <><span className="pal-spinner-sm" /> {statusGeracao || 'Gerando…'}</>
                  : 'Gerar PDF'}
              </button>
            </div>
          </div>
        </div>

        {/* Coluna direita: visualização 3D (fallback 2D se WebGL indisponível) */}
        <div className="pal-detalhe-direita">
          <div className="pal-card pal-viz-card">
            <h3 className="pal-card-title">
              {webGLAvailable ? 'Visualização 3D' : 'Visualização 2D'}
            </h3>
            {webGLAvailable ? (
              <div className="pal-viz3d-wrapper">
                <Visualizacao3D
                  ref={viz3dRef}
                  plano={plano ?? planoInicial}
                  volume={volume}
                  campos={campos}
                  parametros={parametros}
                  modalidade={modalidade}
                  centralizacaoConjuntoZ={centralizacaoConjuntoZ}
                />
                {gerandoPdf && (
                  <div className="pal-viz3d-gerando">
                    <span className="pal-spinner-sm" />
                    <span>{statusGeracao || 'Gerando…'}</span>
                  </div>
                )}
              </div>
            ) : (
              <Visualizacao2D plano={plano ?? planoInicial} volume={volume}
                campos={campos} parametros={parametros} modalidade={modalidade}
                centralizacaoConjuntoZ={centralizacaoConjuntoZ} />
            )}
          </div>
        </div>
      </div>

      {pdfBlobUrl && (
        <ModalPdf
          blobUrl={pdfBlobUrl}
          produto={volume.produto}
          onClose={() => {
            URL.revokeObjectURL(pdfBlobUrl);
            setPdfBlobUrl(null);
          }}
        />
      )}
    </div>
  );
}

export default DetalheVolume;
